import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, Clock, ShoppingBag, MapPin, Star, Truck, MessageCircle, Ghost } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl, whatsappLink, snapchatLink } from "../lib/api";
import { HandDeliveryBadge } from "../components/HandDeliveryBadge";
import { FreeDeliveryBar } from "../components/FreeDeliveryBar";
import { LiveBadge } from "../components/LiveBadge";
import { ProductCard } from "../components/ProductCard";
import { ProductModal } from "../components/ProductModal";
import { ShareButtons } from "../components/ShareButtons";
import { useCart } from "../context/CartContext";
import { useI18n } from "../i18n";
import { MIN_REVIEWS } from "../context/CityContext";
import { useSeoTitle } from "../context/SeoContext";
import { track } from "../lib/analytics";

export default function Storefront() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addItem, count, total } = useCart();
  const { t } = useI18n();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState("Tout");
  const [loading, setLoading] = useState(true);
  useSeoTitle("store", { boutique: vendor?.name || "", ville: vendor?.city || "" }, !!vendor);
  useEffect(() => { if (vendor?.id) track("storefront_view", { meta: { vendor: vendor.name, city: vendor.city } }); }, [vendor?.id, vendor?.name, vendor?.city]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/vendors/${vendorId}`),
      api.get(`/vendors/${vendorId}/products`),
    ]).then(([v, p]) => {
      setVendor(v.data);
      setProducts(p.data);
    }).catch(() => toast.error(t("store.notFound")))
      .finally(() => setLoading(false));
  }, [vendorId, t]);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    const hasPromo = products.some((p) => p.promo);
    return ["Tout", ...(hasPromo ? ["Promo"] : []), ...Array.from(set)];
  }, [products]);

  const visible = activeCat === "Tout" ? products
    : activeCat === "Promo" ? products.filter((p) => p.promo)
    : products.filter((p) => p.category === activeCat);

  const handleAdd = (product) => {
    const ok = addItem(product, {
      id: vendor.id, name: vendor.name, whatsapp: vendor.whatsapp, telegram: vendor.telegram, snapchat: vendor.snapchat,
      phone: vendor.phone, city: vendor.city, lat: vendor.lat, lng: vendor.lng,
      delivery_base: vendor.delivery_base, delivery_per_km: vendor.delivery_per_km,
      free_threshold: vendor.free_threshold, radius_km: vendor.radius_km,
      is_national: !!vendor.is_national, accepts_card: !!vendor.accepts_card,
    });
    if (ok) toast.success(`${product.name} ${t("store.added")}`, { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
  };

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-10 text-zinc-500">{t("store.loading")}</div>;
  if (!vendor) return <div className="max-w-5xl mx-auto px-4 py-10 text-zinc-500">{t("store.notFound")}.</div>;

  const sharedProduct = products.find((p) => p.id === searchParams.get("produit"));

  return (
    <div className={`max-w-5xl mx-auto px-4 sm:px-6 py-8 ${count > 0 ? "pb-28" : ""}`}>
      <button onClick={() => navigate(-1)} data-testid="back-button" className="flex items-center gap-1 text-zinc-400 hover:text-neon-green text-sm uppercase font-semibold mb-6 transition-colors">
        <ChevronLeft className="h-4 w-4" /> {t("store.back")}
      </button>

      {/* Store header */}
      <div className="bg-ink-surface border border-zinc-800 p-6 flex flex-col sm:flex-row gap-5 items-start sm:items-center mb-8">
        <div className="h-20 w-20 rounded-full overflow-hidden border-2 border-neon-green shrink-0 bg-black flex items-center justify-center">
          {vendor.avatar_url ? <img src={imageUrl(vendor.avatar_url)} alt={vendor.name} className="h-full w-full object-cover" /> : <span className="font-anton text-3xl text-neon-green">{vendor.name?.[0]?.toUpperCase()}</span>}
        </div>
        <div className="flex-1">
          <h1 className="font-anton text-3xl uppercase text-white" data-testid="store-name">{vendor.name}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-2">
            <LiveBadge live={vendor.is_live} />
            <span className="flex items-center gap-1 text-zinc-400 text-sm"><Clock className="h-4 w-4 text-neon-magenta" /> {vendor.delivery_time || "< 2h"}</span>
            {vendor.vacation_active ? (
              <span data-testid="store-vacation" className="flex items-center gap-1 text-amber-400 text-sm font-bold">🏖️ {t("st.vacation")} {(vendor.vacation_until || "").split("-").reverse().join("/")}</span>
            ) : vendor.today_off ? (
              <span data-testid="store-rest-today" className="flex items-center gap-1 text-zinc-400 text-sm">😴 {t("st.restToday")}</span>
            ) : vendor.today_hours ? (
              <span data-testid="store-today-hours" className="flex items-center gap-1 text-zinc-400 text-sm">🕑 {t("st.today")} : {vendor.today_hours}</span>
            ) : vendor.opening_hours ? (
              <span className="flex items-center gap-1 text-zinc-400 text-sm" data-testid="store-hours">🕑 {vendor.opening_hours}</span>
            ) : null}
            <span className="flex items-center gap-1 text-zinc-400 text-sm"><MapPin className="h-4 w-4 text-neon-magenta" /> {vendor.city}</span>
            {vendor.rating_count >= MIN_REVIEWS ? (
              <span className="flex items-center gap-1 text-sm" data-testid="vendor-rating">
                <Star className="h-4 w-4 text-neon-green fill-neon-green" />
                <span className="text-white font-bold">{vendor.rating}</span>
                <span className="text-zinc-500">({vendor.rating_count})</span>
              </span>
            ) : vendor.is_national ? null : (
              <span data-testid="new-vendor-badge" className="text-[10px] uppercase font-bold border border-neon-magenta text-neon-magenta px-2 py-0.5">✨ {t("product.newVendor")}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2 text-xs text-zinc-400">
            <Truck className="h-4 w-4 text-neon-green" />
            {vendor.is_national ? (
              <span data-testid="national-delivery-line">Livraison partout en France sous 48h · colis discret{Number(vendor.delivery_base) > 0 ? ` · frais de port ${Number(vendor.delivery_base).toFixed(2)}€` : " · frais de port offerts"}{vendor.free_threshold > 0 && Number(vendor.delivery_base) > 0 ? ` (offerts dès ${Number(vendor.free_threshold).toFixed(0)}€)` : ""}</span>
            ) : (
            <span>{t("store.delivery")} {Number(vendor.delivery_base).toFixed(2)}€ ({t("store.kmIncl")}) + {Number(vendor.delivery_per_km).toFixed(2)}€/km — <span className="text-neon-green font-semibold">{t("store.freeFrom")} {Number(vendor.free_threshold).toFixed(0)}€</span> · {t("store.zone")} {vendor.radius_km} km</span>
            )}
          </div>
          {vendor.description ? <p className="text-zinc-500 text-sm mt-2">{vendor.description}</p> : null}
          <HandDeliveryBadge compact className="mt-3" national={!!vendor.is_national} acceptsCard={!!vendor.accepts_card} />
          {(vendor.whatsapp || vendor.phone || vendor.snapchat) && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="store-contact-buttons">
              <span className="w-full text-[11px] uppercase font-bold text-zinc-500">Une question ? Contacte ton livreur</span>
              {(vendor.whatsapp || vendor.phone) && (
                <a data-testid="store-contact-whatsapp" onClick={() => track("whatsapp_click", { meta: { where: "storefront" } })} href={whatsappLink(vendor.whatsapp || vendor.phone, `Salut ${vendor.name} ! Je suis sur ta boutique STUFF CBD (${vendor.city}), j'ai une question 🌿`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-neon-green text-black font-anton uppercase text-sm px-4 py-2.5 hover:bg-white transition-colors"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
              )}
              {vendor.snapchat && (
                <a data-testid="store-contact-snapchat" onClick={() => track("snap_click", { meta: { where: "storefront" } })} href={snapchatLink(vendor.snapchat)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#FFFC00] text-black font-anton uppercase text-sm px-4 py-2.5 hover:bg-yellow-300 transition-colors"><Ghost className="h-4 w-4" /> Snapchat</a>
              )}
            </div>
          )}
          <div className="mt-3">
            <ShareButtons
              url={`${window.location.origin}/api/share/boutique/${vendor.id}`}
              title={`${vendor.name} — Livraison CBD à ${vendor.city} 🌿 Commande en ligne, paiement cash à la livraison sur STUFF CBD.`}
              label="Partager la boutique"
            />
          </div>
        </div>
      </div>

      {/* Bandeau légalité */}
      <div data-testid="legal-reassurance-bar" className="flex flex-wrap items-center gap-x-4 gap-y-1 border border-neon-green/30 bg-neon-green/5 px-4 py-2.5 mb-6 text-xs text-zinc-300">
        <span className="font-bold text-neon-green">🌿 {t("store.legal1")}</span>
        <span>🔬 {t("store.legal2")}</span>
        <span>🔞 {t("store.legal3")}</span>
      </div>

      {!vendor.is_live && (
        <div className="border border-neon-magenta/50 bg-neon-magenta/10 text-neon-magenta text-sm px-4 py-3 mb-6">
          {t("store.offline")} <span className="font-bold">{t("store.offlineB")}</span>{t("store.offlineC")}
        </div>
      )}

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar scroll-fade-x mb-6 pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`category-${c}`}
            onClick={() => setActiveCat(c)}
            className={`shrink-0 px-4 py-2 uppercase text-xs font-bold tracking-wide border transition-all ${activeCat === c ? "bg-neon-green text-black border-neon-green" : "bg-transparent text-zinc-400 border-zinc-700 hover:border-neon-green hover:text-neon-green"}`}
          >
            {c === "Tout" ? t("store.all") : c === "Promo" ? t("store.promo") : c}
          </button>
        ))}
      </div>

      {/* Products */}
      {visible.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center">{t("store.noProduct")}</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((p) => <ProductCard key={p.id} product={p} onAdd={handleAdd} />)}
        </div>
      )}

      {/* Floating cart bar */}
      {count > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 p-4 pb-safe">
          {vendor?.free_threshold > 0 && <div className="max-w-md mx-auto mb-2"><FreeDeliveryBar subtotal={total} threshold={vendor.free_threshold} compact /></div>}
          <Link
            to="/panier"
            data-testid="go-to-cart"
            className="max-w-md mx-auto flex items-center justify-between bg-neon-green text-black font-anton uppercase px-6 py-4 glow-green hover:bg-neon-greenDark transition-colors"
          >
            <span className="flex items-center gap-2"><ShoppingBag className="h-5 w-5" /> {t("store.viewCart")}</span>
            <span>{count} {count > 1 ? t("store.items") : t("store.item")}</span>
          </Link>
        </div>
      )}
      {/* Deep link produit partagé (?produit=) */}
      {sharedProduct && (
        <ProductModal product={sharedProduct} onAdd={handleAdd} onClose={() => setSearchParams({}, { replace: true })} />
      )}
    </div>
  );
}
